/**
 * Walks a list of image URLs, best first, stepping down to the next whenever one fails to
 * load — the UI half of `@rista/core/media/imageUrls`, which decides the order.
 *
 * It was `useMediaUrlFallback` inside `@rista/ui/components/CustomerDisplay/hooks.ts`, tied
 * to a `ResolvedMediaItem`. Generalised to a plain `string[]` (2026-09-09) so the kiosk's
 * item artwork can step through the same candidates; the customer display's hook now
 * delegates here, so there is one definition of the stepping rather than two.
 *
 * No platform API — `useState` and `useCallback` — so `shared.ts` owns it and both variants
 * re-export, the shape `hooks/useDebounce` established for a kiosk-driven hook both
 * platforms need.
 */
import { useCallback, useEffect, useState } from "react";

export interface ImageUrlFallback {
  /** The candidate to render now. `undefined` when there are none left to try. */
  url?: string;
  /** Hand this to the element's `onError`. */
  handleError: () => void;
  /** True once every candidate has failed — draw a placeholder instead of a broken box. */
  exhausted: boolean;
}

export function useImageUrlFallback(candidates: string[]): ImageUrlFallback {
  /**
   * The reset keys off the URLs THEMSELVES rather than the array identity. Both callers
   * rebuild the list on every render — the customer display from a fresh payload push, the
   * kiosk from a `useMemo` over store data — so keying on identity would bounce back to a URL
   * already known to 404 on every unrelated update.
   */
  const candidateKey = candidates.join("|");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [candidateKey]);

  /**
   * Steps PAST the last candidate rather than clamping to it. The old customer-display
   * version clamped, which left a known-broken URL on the element and no way for a caller to
   * tell "still trying" from "nothing here" — `exhausted` is what a placeholder branches on.
   */
  const handleError = useCallback(() => {
    setIndex((current) => (current < candidates.length ? current + 1 : current));
  }, [candidates.length]);

  return {
    url: candidates[index],
    handleError,
    exhausted: index >= candidates.length,
  };
}

export default useImageUrlFallback;
